import type { AuditEventRecord } from "@packages/global";

export type NotificationTone = "brand" | "success" | "warning" | "danger" | "neutral" | "info";

export interface ActivityNotification {
  id: string;
  title: string;
  description: string;
  time: string;
  tone: NotificationTone;
  badge: string;
  isActionRequired: boolean;
  targetPath: string | null;
  source: AuditEventRecord;
}

const actionLabels: Record<string, string> = {
  "auth.user.logged_in": "账号登录成功",
  "auth.user.login_failed": "账号登录失败",
  "warehouse.reparse.created": "数据仓库重新解析已提交",
  "warehouse.reparse.cancelled": "数据仓库重新解析已取消",
  "warehouse.export.created": "数据仓库导出已创建",
  "warehouse.export.cancelled": "数据仓库导出已取消",
  "warehouse.export.downloaded": "数据仓库导出已下载",
  "source.export.created": "数据源导出已创建",
  "source.export.cancelled": "数据源导出已取消",
  "source.export.downloaded": "数据源导出已下载",
  "source.import_record.created": "数据导入记录已创建",
  "source.import_record.files_uploaded": "数据文件已上传",
  "source.import_record.deleted": "数据导入记录已删除",
  "research.created": "研究已创建",
  "research.updated": "研究配置已更新",
  "research.deleted": "研究已删除",
  "research.run.created": "研究运行已提交",
  "research.run.cancelled": "研究运行已取消",
  "research.export.created": "研究导出已创建",
  "research.export.cancelled": "研究导出已取消",
  "research.export.downloaded": "研究导出已下载",
  "quality.run.created": "质控运行已提交",
  "query.created": "数据质疑已发起",
  "query.responded": "数据质疑已响应",
  "query.closed": "数据质疑已关闭",
  "query.reopened": "数据质疑已重新打开",
  "query.cancelled": "数据质疑已撤销",
  "query.assigned": "数据质疑已指派",
  "project.member.invited": "协作邀请",
  "project.member.added": "项目权限已开通",
  "admin.project.member.added": "项目成员已添加",
  "admin.project.member.updated": "项目成员权限已变更",
  "admin.project.member.disabled": "项目成员已停用",
  "发起数据质疑": "数据质疑已发起",
  "响应数据质疑": "数据质疑已响应",
  "关闭数据质疑": "数据质疑已关闭",
  "重新打开数据质疑": "数据质疑已重新打开",
  "撤销数据质疑": "数据质疑已撤销",
  "改派数据质疑": "数据质疑已指派",
  "关联人工修正关闭质疑": "数据质疑已更正并关闭",
};

export function getAuditActionLabel(action: string): string {
  return actionLabels[action] ?? action;
}

export function toNotification(event: AuditEventRecord): ActivityNotification {
  const isFailed = event.result !== "success";
  const isWarning = event.severity === "warning";
  const title = getAuditActionLabel(event.action);
  const projectPart = event.projectName ? `项目：${event.projectName}` : "平台事件";
  const targetPart = event.targetName || event.targetId || event.targetType || event.module;
  const description = `${projectPart} · ${targetPart}`;
  const isQuery = event.module === "query";
  const isExport = event.action.includes(".export.");
  const isRun = event.action.includes(".run.");
  const isInvite = event.action.includes("member.");
  const tone: NotificationTone = isFailed
    ? "danger"
    : isWarning || isQuery
      ? "warning"
      : isExport
        ? "success"
        : isRun
          ? "brand"
          : isInvite
            ? "info"
            : "neutral";
  const badge = isFailed
    ? "失败"
    : isQuery
      ? "质疑"
      : isExport
        ? "导出"
        : isRun
          ? "运行"
          : isInvite
            ? "协作"
            : isWarning
              ? "需关注"
              : "系统";

  return {
    id: event.id,
    title,
    description,
    time: event.occurredAt,
    tone,
    badge,
    isActionRequired: isFailed || isWarning || isQuery,
    targetPath: getTargetPath(event),
    source: event,
  };
}

export function getTargetPath(event: AuditEventRecord): string | null {
  return null;
}

export function isActionRequired(event: AuditEventRecord): boolean {
  return event.result !== "success" || event.severity === "warning" || event.module === "query";
}
